import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react-shared/Icon";
import { Button } from "@circle-react-uikit/Button";
import { DescriptionEditor } from "./DescriptionEditor";
import { EmptyQuestionsBlock } from "./EmptyQuestionsBlock";
import { QuestionItem } from "./QuestionItem";
import type { QuestionPayload } from "./QuestionModal";
import { useQuestionModal } from "./QuestionModal";

const DEFAULT_QUESTION: QuestionPayload = {
  question_type: "single_choice",
  cover_media: null,
  options: [
    { correct: true, value: `${t("courses.edit.lesson.option")} 1` },
    { correct: false, value: `${t("courses.edit.lesson.option")} 2` },
    { correct: false, value: `${t("courses.edit.lesson.option")} 3` },
  ],
};

export const QuizContent = () => {
  const questionModal = useQuestionModal();
  const { getValues, watch } = useFormContext();

  const {
    fields: questions,
    append,
    update,
    move,
  } = useFieldArray({
    name: "quiz[questions]",
  });

  const questionObjects: QuestionPayload[] = watch("quiz[questions]");
  const visibleQuestions = questionObjects.filter(q => !q._destroy);
  const reversedQuestions = [...questionObjects].reverse();
  const firstVisibleIndex = questionObjects.findIndex(q => !q._destroy);
  const lastVisibleIndex =
    questionObjects.length - 1 - reversedQuestions.findIndex(q => !q._destroy);

  const addQuestion = () => {
    void questionModal.show({
      question: DEFAULT_QUESTION,
      questionIndex: questions.length,
      onSubmit: (_questionIndex, question) => append(question),
      isEdit: false,
    });
  };

  const editQuestion = (questionIndex: number) => {
    const question = getValues(`quiz.questions[${questionIndex}]`);
    void questionModal.show({
      question,
      questionIndex,
      onSubmit: (questionIndex, question) => update(questionIndex, question),
      isEdit: true,
    });
  };

  return (
    <>
      <DescriptionEditor />
      {visibleQuestions.length > 0 ? (
        <>
          {questions.map((question, index) => (
            <QuestionItem
              key={question.id}
              questionIndex={index}
              onDelete={update}
              onEdit={() => editQuestion(index)}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              firstVisibleIndex={firstVisibleIndex}
              lastVisibleIndex={lastVisibleIndex}
            />
          ))}
          <Button type="button" variant="circle" full onClick={addQuestion}>
            <Icon type="plus" useWithFillCurrentColor className="mr-2" />
            {t("courses.edit.lesson.add_question")}
          </Button>
        </>
      ) : (
        <EmptyQuestionsBlock onClick={addQuestion} />
      )}
    </>
  );
};
